import React from 'react';
import { useParams } from 'react-router-dom';
import { ArchiveBoxIcon, PlayIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { fadeIn } from '../../../lib/motion';
import { isPending } from '../../courses/editors/contentPlaceholder';
import { createScormSession, absolutePlayerUrl } from '../../../services/scorm';
import ModuleViewContainer from './ModuleViewContainer';

export interface ScormViewProps {
  item: { file_url?: string; title?: string; id?: string; module_id?: string };
  mode: 'learner' | 'preview';
}

/**
 * SCORM packages run in the backend-served player (same origin as the
 * package assets, SCORM 1.2 API, per-enrollment save/resume). The view
 * mints a session on launch; completion flows back via postMessage and
 * the module page's normal completion pipeline.
 */
const ScormView: React.FC<ScormViewProps> = ({ item, mode }) => {
  const { assignmentId, moduleId: routeModuleId } = useParams<{ assignmentId: string; moduleId: string }>();
  const [playerUrl, setPlayerUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [launching, setLaunching] = React.useState(false);

  const moduleId = routeModuleId || item.module_id || item.id;
  const ready =
    !!moduleId && !!item.file_url && !isPending(item.file_url) && !String(item.file_url).startsWith('http');

  const launch = async () => {
    if (!moduleId || launching) return;
    setLaunching(true);
    setError(null);
    try {
      const session = await createScormSession(moduleId, {
        assignmentId: mode === 'learner' ? assignmentId : undefined,
        preview: mode !== 'learner',
      });
      setPlayerUrl(absolutePlayerUrl(session.player_url));
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Could not start this package. Please try again.';
      setError(message);
    } finally {
      setLaunching(false);
    }
  };

  const body = playerUrl ? (
    // The package fills the card rather than sitting in a 480px band inside it
    // (Aleix, 2026-09-12: "it fills out just half of the card"). The frame is
    // absolutely positioned so it takes whatever height the card gives it,
    // however the flex chain above resolves; the viewport floor keeps it
    // generous on a page that never constrains its height. Preview gets the
    // same treatment: an admin checking a package was being shown a thumbnail
    // of it (Aleix, 2026-09-12).
    <div
      className={`relative flex-1 rounded-2xl overflow-hidden bg-black/90 shadow-[0_4px_10px_rgba(0,0,0,0.1)] ${
        mode === 'learner' ? 'min-h-[82vh]' : 'min-h-[70vh]'
      }`}
    >
      <iframe
        src={playerUrl}
        title={item.title || 'SCORM package'}
        className="absolute inset-0 w-full h-full border-0"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        allow="autoplay; fullscreen"
      />
    </div>
  ) : (
    <div className="flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl bg-primary-50 dark:bg-primary-900/10 border border-primary-200 dark:border-primary-800">
      <ArchiveBoxIcon className="w-10 h-10 text-primary-600 dark:text-primary-350 shrink-0" />
      <div className="flex-1 text-center sm:text-left">
        <p className="font-medium text-text-primary">Interactive SCORM Package</p>
        <p className="text-sm text-text-secondary">
          {ready
            ? 'Your progress and score are saved automatically — you can leave and resume anytime.'
            : 'This module has no uploaded package yet.'}
        </p>
        {error && <p className="text-sm text-error-500 mt-1">{error}</p>}
      </div>
      <button
        type="button"
        onClick={launch}
        disabled={!ready || launching}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 disabled:opacity-50 active:scale-[0.97] transition-[background-color,transform] duration-150"
        data-testid="scorm-launch"
      >
        <PlayIcon className="w-4 h-4" />
        {launching ? 'Starting…' : 'Start package'}
      </button>
    </div>
  );

  if (mode === 'learner') {
    return (
      <motion.div {...fadeIn} className="w-full flex-1 flex flex-col min-h-0">
        <ModuleViewContainer padding="px-4 pb-4">
          {item.title && (
            <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3">
              <h2 className="text-lg sm:text-xl font-bold text-text-primary leading-tight">
                {item.title}
              </h2>
            </div>
          )}
          <div className="flex-1 flex flex-col min-h-0 px-3 sm:px-4 pb-3 sm:pb-4">{body}</div>
        </ModuleViewContainer>
      </motion.div>
    );
  }

  return (
    <motion.div {...fadeIn} className="w-full flex-1 flex flex-col min-h-0">
      {body}
    </motion.div>
  );
};

export default ScormView;
